import type {
  StoredMessage,
  StoredDiskMessage,
  StoredThread,
} from '@/lib/assistant-cloud-local/types';
import { decryptJson, encryptJson, hasKey, isEncrypted } from '@/lib/history-key/cipher';
import { InvalidHistoryMarkerError } from '@/lib/history-key/errors';
import { HISTORY_OWNER } from '@/lib/history-key/owner';

/** The server marks a turn that reached account data with a `data-protected` part naming the owner. */
export function ownerFromParts(content: unknown): string | undefined {
  const parts =
    (content as { parts?: { type?: string; data?: { owner?: unknown } }[] } | null)?.parts ?? [];
  const marker = parts.find(part => part.type === 'data-protected');
  if (!marker) return undefined;
  const owner = marker.data?.owner;
  if (typeof owner !== 'string' || !HISTORY_OWNER.test(owner))
    throw new InvalidHistoryMarkerError();
  return owner;
}

export async function sealMessage({
  thread,
  message,
}: {
  thread: Pick<StoredThread, 'owner'>;
  message: StoredDiskMessage;
}): Promise<StoredDiskMessage> {
  if (!thread.owner || isEncrypted(message.content)) return message;
  return {
    ...message,
    content: await encryptJson({
      owner: thread.owner,
      value: message.content,
      context: [message.thread_id, message.id],
    }),
  };
}

export async function openMessage(message: StoredDiskMessage): Promise<StoredMessage> {
  if (!isEncrypted(message.content)) return { ...message, content: message.content };
  return {
    ...message,
    content: await decryptJson({
      record: message.content,
      context: [message.thread_id, message.id],
    }),
  };
}

export async function sealThread(thread: StoredThread): Promise<StoredThread> {
  if (!thread.owner || thread.title_enc) return thread;
  return {
    ...thread,
    title: '',
    title_enc: await encryptJson({
      owner: thread.owner,
      value: thread.title,
      context: [thread.id],
    }),
  };
}

export async function openThread(thread: StoredThread): Promise<StoredThread> {
  if (!thread.title_enc) return thread;
  return {
    ...thread,
    title: await decryptJson<string>({ record: thread.title_enc, context: [thread.id] }),
    title_enc: undefined,
  };
}

/** Threads whose owner key is not held (or does not open) stay out of memory until it is. */
export async function openThreads(threads: StoredThread[]): Promise<StoredThread[]> {
  const opened: StoredThread[] = [];
  for (const thread of threads) {
    if (thread.owner && !hasKey(thread.owner)) continue;
    try {
      opened.push(await openThread(thread));
    } catch {
      // The held key does not open this title (another key, or damaged ciphertext); hidden until it does.
    }
  }
  return opened;
}
